import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getApiUrl, API_ENDPOINTS } from '@shared/utils/app-util';

export interface ManageAssessment {
  rca_id: number,
  version_number: any,
  change_assess_no: string,
  title?: string,
  ecn_ern: string,
  change_types: string[],
  product_families: string[],
  start_date: string,
  due_date: string,
  status: string,
  countries: string[],
  region_id?: number,
  display_status?: string,
  display_progress?: string,
  progress?: any,
  is_owner?: boolean;
  response_status?: string;
  response_mapping_id?: any;
  owner_user_id?: any;
  shared_user_ids?: any;
  // Add any other fields you need from the response
}

export interface GetManageAssessmentResponse {
  message: string;
  rcas: ManageAssessment[];
  meta: {
    total: number,
    page: number,
    page_size: number,
    total_pages: number,
    count: number
  },
  status_counts: {
    total: number,
    draft: number,
    submitted: number,
    completed: number,
    cancelled_deleted: number
  },
  aggregated_status_counts: {
    total: number,
    yet_to_start: number,
    ongoing: number,
    completed: number,
    expired: number
  };
}

export interface GetRcaVersionHistoryResponse {
  message: string,
  rca_id: number,
  history: any
}

export interface GetResponseVersionHistoryResponse {
  message: string,
  response_mapping_id: number,
  rca_version_id: number,
  country_id: number,
  history: any
}

export interface ActionManageAssessmentResponse{
  message: string,
  rca_id: number,
  rca_version: number,
  change_assess_no: string,
  status: string
}

export interface GetManageAssessmentDraftResponse {
  message: string;
  rca_id: number;
  rca_version: number;
  change_assess_no: string;
  status: string
}

export interface DeviceClassifications {
  id: number;
  name: string;
  description: string;
}

export interface ClassificationsStatus {
  id: number;
  name: string;
}

export interface ProductDesignHistory {
  id: number;
  name: string;
}

export interface ChangeTypes {
  id: number;
  name: string;
  category_id: number;
  category: {
    id: number;
    name: string;
  }
}

export interface ProductFamilies {
  id: number;
  name: string;
  added_at: string;
}

export interface GetClassificationResponse {
  message: string;
  classifications_status: ClassificationsStatus[];
}

export interface GetUsDeviceClassificationsResponse {
  message: string;
  us_device_classifications: DeviceClassifications[];
}

export interface GetEuDeviceClassificationsResponse {
  message: string;
  eu_device_classifications: DeviceClassifications[];
}

export interface GetProductDesignHistoryResponse {
  message: string;
  product_design_history_docs: ProductDesignHistory[];
}

export interface GetChangeTypesResponse {
  message: string;
  change_types: ChangeTypes[];
}

export interface GetProductFamilyResponse {
  message: string;
  product_families: ProductFamilies[];
}

export interface AttachmentData {
  bucket_name?: string;
  file_path?: string;
  id?: number;
  file_name?: string;
}


export interface ManageAssessmentRequestPayload {
  attachment_description: any,
  attachment_data: AttachmentData[],
  catalogue_no: any,
  change_type_ids: number[],
  country_ids: number[],
  description: any,
  design_history_doc_ids: number[],
  other_design_docs?: any, // New field for custom "Other Design Docs"
  due_date: any,
  ecn_ern: any,
  eu_class_status: any,
  eu_med_class_id: any,
  issue_date: any,
  planned_timeline: any,
  product_family_ids: number[],
  region_ids: number[],
  title?: any,
  us_class_status: any,
  us_med_class_id: any,
  device_classification?: any;
  support_document?: any;
  device_attachment_description?: any;
  impact_conclusion?: any;
  time_submissions?: string;
  impact_assess_justify?: string;
  time_approve?: string;
  product_authority?: string;
  product_notify?: string;
}

export interface NoImpactRequestPayload {
  response_mapping_ids: number[]
}

export interface ManageAssessmentByRcaResponse {
  id: number,
  change_assess_no: string,
  title?: string,
  version_number: number,
  region_id: number,
  ecn_ern: string,
  catalogue_no: string,
  us_class_status: string,
  us_med_class_id: string,
  eu_class_status: string,
  eu_med_class_id: string,
  issue_date: string,
  due_date: string,
  description: string,
  planned_timeline: string,
  status_id: number,
  status_name: string,
  published_at: string,
  created_at: string,
  created_by: number,
  created_by_name: string,
  system_remark: string,
  change_types: string[],
  product_families: string[],
  design_history_docs: string[],
  countries: string[],
  attachments: object[]
}

export interface AssessmentItem {
  s_no: number;
  response_mapping_id: number;
  country_id: number;
  region_name: string;
  country_name: string;
  local_ra: string;
  impact_status: string;
  response_status: string;
  timestamp: any;
  version: string;
  assigned_to_user_id: number;
}

export interface ResponseFormApiPayload {
  device_class: number;
  impact_conclusion: boolean;
  impact_assess_justification: string;
  prior_approval_acceptance?: boolean;
  post_approval_acceptance?: boolean;
  local_authority_eta?: any;
  health_authority_eta?: any;
  time_product_notify?: any;
  response_content: string;
  attachment_data: any[];
  documentation_required: any;
  other_documentation: any;
}

export interface ResponseManageAssessmentByRcaResponse {
  message: string,
  rca_id: number,
  change_assess_no: string,
  rca_version: number,
  metrics: {
    total_countries: number,
    responded_countries: number,
    pending_countries: number
  },
  response_forms: [
    {
      response_mapping_id: number,
      country_id: number,
      ra_colleague_names: string[],
      impact_status: string,
      response_status: string,
      started_at_timestamp: string,
      received_at_timestamp: string,
      response_version_number: number,
      assigned_to_user_id: number,
    }
  ],
  meta: {
    total: number,
    page: number,
    page_size: number,
    total_pages: number,
    count: number
  }
}

@Injectable({
  providedIn: "root",
})
export class ManageAssessmentApiService {
  rcaFormEnabled = new BehaviorSubject<Object>({});
  navigateToTab = new BehaviorSubject<any>('');
  constructor(private http: HttpClient) {}

  /**
   * Fetches a list of manage assessment with pagination and filtering.
   * @param page The current page number.
   * @param pageSize The number of items per page.
   * @param filters Optional filters (e.g., status, start date, due date ).
   * @returns An Observable of the manage assessment response.
   */
  getManageAssessment(
    page: number,
    pageSize: number,
    filters?: any
  ): Observable<GetManageAssessmentResponse> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString());

    // Add filters to params if they exist
    if (filters) {
      for (const key in filters) {
        if (
          filters.hasOwnProperty(key) &&
          filters[key] !== null &&
          filters[key] !== undefined &&
          filters[key] !== ""
        ) {
          // Handle array filters (e.g., multiselects)
          if (Array.isArray(filters[key])) {
            filters[key].forEach((item: any) => {
              params = params.append(key, item); // Append each item in the array
            });
          } else if (
            typeof filters[key] === "object" &&
            filters[key].from &&
            filters[key].to
          ) {
            // Handle date range filters (assuming 'from' and 'to' properties)
            // Convert Date objects to ISO strings for API
            params = params.set(`${key}_from`, filters[key].from.toISOString());
            params = params.set(`${key}_to`, filters[key].to.toISOString());
          } else {
            params = params.set(key, filters[key]);
          }
        }
      }
    }
    const url = getApiUrl("GET_MANAGE_ASSESSMENT");
    return this.http.get<GetManageAssessmentResponse>(url, { params });
  }

  /**
   * Create RCA manage assessment.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  createManageAssessment(
    payload: ManageAssessmentRequestPayload
  ): Observable<GetManageAssessmentResponse> {
    const url = getApiUrl("CREATE_MANAGE_ASSESSMENT");
    return this.http.post<GetManageAssessmentResponse>(url, payload);
  }

  /**
   * Create RCA manage assessment as draft.
   * @param payload RCA assessment request data payload.
   * @returns An Observable of the rca draft response.
   */
  createDraftManageAssessment(
    payload: ManageAssessmentRequestPayload
  ): Observable<GetManageAssessmentDraftResponse> {
    const url = getApiUrl("CREATE_MANAGE_ASSESSMENT_DRAFT");
    return this.http.post<GetManageAssessmentDraftResponse>(url, payload);
  }

  /**
   * Update existing RCA manage assessment (standard edit).
   * @param payload RCA assessment request data payload.
   * @param rcaId Existing RCA id to update.
   * @returns An Observable of the updated rca response.
   */
  updateManageAssessment(
    payload: ManageAssessmentRequestPayload,
    rcaId: number
  ): Observable<GetManageAssessmentResponse> {
    const url = `${getApiUrl("API_RCA")}/${rcaId}`;
    return this.http.put<GetManageAssessmentResponse>(url, payload);
  }

  /**
   * Fetches the list of classification status from the backend.
   * @returns An Observable of the classification status response.
   */
  getClassifications(): Observable<GetClassificationResponse> {
    const url = getApiUrl("GET_CLASSIFICATIONS_STATUS");
    return this.http.get<GetClassificationResponse>(url);
  }

  /**
   * Fetches the list of us device classification from the backend.
   * @returns An Observable of the us device classification response.
   */
  getUsDeviceClassifications(): Observable<GetUsDeviceClassificationsResponse> {
    const url = getApiUrl("GET_US_DEVICE_CLASSIFICATIONS");
    return this.http.get<GetUsDeviceClassificationsResponse>(url);
  }

  /**
   * Fetches the list of eu device classification from the backend.
   * @returns An Observable of the eu device classification response.
   */
  getEuDeviceClassifications(): Observable<GetEuDeviceClassificationsResponse> {
    const url = getApiUrl("GET_EU_DEVICE_CLASSIFICATIONS");
    return this.http.get<GetEuDeviceClassificationsResponse>(url);
  }

  /**
   * Fetches the list of product design history doc from the backend.
   * @returns An Observable of the product design history doc response.
   */
  getProductDesignHistoryDoc(): Observable<GetProductDesignHistoryResponse> {
    const url = getApiUrl("GET_PRODUCT_DESIGN_HISTORY_DOCS");
    return this.http.get<GetProductDesignHistoryResponse>(url);
  }

  /**
   * Fetches the list of change types from the backend.
   * @returns An Observable of the change types response.
   */
  getChangeTypes(): Observable<GetChangeTypesResponse> {
    const url = getApiUrl("GET_CHANGE_TYPES");
    return this.http.get<GetChangeTypesResponse>(url);
  }

  /**
   * Fetches the list of product families from the backend.
   * @returns An Observable of the product families response.
   */
  getProductFamilies(): Observable<GetProductFamilyResponse> {
    const url = getApiUrl("GET_PRODUCT_FAMILIES");
    return this.http.get<GetProductFamilyResponse>(url);
  }

  /**
   * Complete RCA manage assessment.
   * @param rcaId RCA assessment rca id.
   * @returns An Observable of the rca response.
   */
  completeManageAssessment(
    rcaId: number
  ): Observable<ActionManageAssessmentResponse> {
    const url = getApiUrl("COMPLETE_MANAGE_ASSESSMENT");
    const finalUrl = `${url}/${rcaId}`;
    return this.http.put<ActionManageAssessmentResponse>(finalUrl, {});
  }

  /**
   * Cancel RCA manage assessment.
   * @param rcaId RCA assessment rca id.
   * @returns An Observable of the rca response.
   */
  cancelManageAssessment(
    rcaId: number
  ): Observable<ActionManageAssessmentResponse> {
    const url = getApiUrl("CANCEL_MANAGE_ASSESSMENT");
    const finalUrl = `${url}/${rcaId}`;
    return this.http.put<ActionManageAssessmentResponse>(finalUrl, {});
  }

  getManageAssessmentByRca(
    rcaId: number
  ): Observable<ManageAssessmentByRcaResponse> {
    const url = getApiUrl("GET_MANAGE_ASSESSMENT_BY_RCA");
    const finalUrl = `${url}/${rcaId}`;
    return this.http.get<ManageAssessmentByRcaResponse>(finalUrl);
  }

  getRcaDetailsResponse(
    rcaId: number,
    page: number,
    pageSize: number,
    filters?: any,
    historyId?: number
  ): Observable<ResponseManageAssessmentByRcaResponse> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("page_size", pageSize.toString());

    // Add history_id if provided
    if (historyId) {
      params = params.set("history_id", historyId.toString());
    }

    if (filters) {
      for (const key in filters) {
        if (
          filters.hasOwnProperty(key) &&
          filters[key] !== null &&
          filters[key] !== undefined &&
          filters[key] !== ""
        ) {
          if (Array.isArray(filters[key])) {
            filters[key].forEach((item: any) => {
              params = params.append(key, item);
            });
          } else if (
            typeof filters[key] === "object" &&
            filters[key].from &&
            filters[key].to
          ) {
            params = params.set(`${key}_from`, filters[key].from.toISOString());
            params = params.set(`${key}_to`, filters[key].to.toISOString());
          } else {
            params = params.set(key, filters[key]);
          }
        }
      }
    }
    const url = getApiUrl("API_RCA");
    const finalUrl = `${url}/${rcaId}/response-forms`;
    return this.http.get<ResponseManageAssessmentByRcaResponse>(finalUrl, {
      params,
    });
  }

  /**
   * Revise RCA manage assessment submit.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  reviseManageAssessment(
    payload: ManageAssessmentRequestPayload,
    rcaId: number
  ): Observable<GetManageAssessmentResponse> {
    const url = `${getApiUrl("REVISE_MANAGE_ASSESSMENT")}/${rcaId}`;
    return this.http.post<GetManageAssessmentResponse>(url, payload);
  }

  /**
   * Revise RCA manage assessment as draft.
   * @param payload RCA assessment request data payload.
   * @returns An Observable of the rca draft response.
   */
  reviseDraftManageAssessment(
    payload: ManageAssessmentRequestPayload,
    rcaId: number
  ): Observable<GetManageAssessmentDraftResponse> {
    const url = `${getApiUrl("REVISE_MANAGE_ASSESSMENT_DRAFT")}/${rcaId}`;
    return this.http.post<GetManageAssessmentDraftResponse>(url, payload);
  }

  /**
   * Resume Revise RCA manage assessment submit.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  resumeReviseManageAssessment(
    payload: ManageAssessmentRequestPayload,
    rcaId: number
  ): Observable<GetManageAssessmentResponse> {
    const url = `${getApiUrl("API_RCA")}/${rcaId}`;
    return this.http.put<GetManageAssessmentResponse>(url, payload);
  }

  /**
   * Resume submit RCA manage assessment submit.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  resumeCreateManageAssessment(
    payload: ManageAssessmentRequestPayload,
    rcaId: number
  ): Observable<GetManageAssessmentResponse> {
    const url = `${getApiUrl("RESUME_MANAGE_ASSESSMENT_DRAFT")}/${rcaId}`;
    return this.http.put<GetManageAssessmentResponse>(url, payload);
  }

  /**
   * Resume submit RCA manage assessment submit.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  getRcaVersionHistory(
    rcaId: number
  ): Observable<GetRcaVersionHistoryResponse> {
    const url = `${getApiUrl("RCA_VERSION_HISTORY")}/${rcaId}`;
    return this.http.get<GetRcaVersionHistoryResponse>(url);
  }

  /**
   * Get response version history.
   * @param responseMappingId Response mapping ID.
   * @returns An Observable of the response version history.
   */
  getResponseVersionHistory(
    responseMappingId: number
  ): Observable<GetResponseVersionHistoryResponse> {
    const url = `${getApiUrl("RESPONSE_VERSION_HISTORY")}/${responseMappingId}/history`;
    return this.http.get<GetResponseVersionHistoryResponse>(url);
  }

  /**
   * Get RCA assessment by history_id.
   * @param rcaId RCA assessment rca id.
   * @param historyId History id to fetch specific version.
   * @returns An Observable of the rca response.
   */
  getManageAssessmentByRcaHistory(
    rcaId: number,
    historyId: number
  ): Observable<ManageAssessmentByRcaResponse> {
    const url = getApiUrl("GET_MANAGE_ASSESSMENT_BY_RCA");
    let params = new HttpParams().set("history_id", historyId.toString());
    const finalUrl = `${url}/${rcaId}`;
    return this.http.get<ManageAssessmentByRcaResponse>(finalUrl, { params });
  }

  /**
   * Fetch Response RCA data by mapping id.
   * @param payload RCA mapping id.
   * @returns An Observable of the rca mapping response.
   */
  getResponseRcaForm(mappingId: number): Observable<any> {
    const url = `${getApiUrl("RESPONSE_RCA_MAPPING_BY_ID")}/${mappingId}/view`;
    return this.http.get<any>(url);
  }

  /**
   * Mark as No Impact an assessment.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  updateRcaAsNoImpact(payload: NoImpactRequestPayload): Observable<any> {
    const url = getApiUrl("RCA_AS_NO_IMPACT");
    return this.http.post<any>(url, payload);
  }

  /**
   * RCA response form save data.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  saveRcaResponseForm(
    payload: ResponseFormApiPayload,
    mappingId: number
  ): Observable<any> {
    const url = `${getApiUrl(
      "RESPONSE_RCA_MAPPING_BY_ID"
    )}/${mappingId}/save-progress`;
    return this.http.post<any>(url, payload);
  }

  /**
   * RCA response form save revise data.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  saveRcaReviseResponseForm(
    payload: ResponseFormApiPayload,
    mappingId: number
  ): Observable<any> {
    const url = `${getApiUrl(
      "RESPONSE_RCA_MAPPING_BY_ID"
    )}/${mappingId}/revise-draft`;
    return this.http.post<any>(url, payload);
  }

  /**
   * RCA response form save data.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  submitRcaResponseForm(
    payload: ResponseFormApiPayload,
    mappingId: number
  ): Observable<any> {
    const url = `${getApiUrl(
      "RESPONSE_RCA_MAPPING_BY_ID"
    )}/${mappingId}/submit`;
    return this.http.post<any>(url, payload);
  }

  /**
   * RCA response form save revise data.
   * @param payload RCA assessment request data.
   * @returns An Observable of the rca response.
   */
  submitRcaReviseResponseForm(
    payload: ResponseFormApiPayload,
    mappingId: number
  ): Observable<any> {
    const url = `${getApiUrl(
      "RESPONSE_RCA_MAPPING_BY_ID"
    )}/${mappingId}/revise-submit`;
    return this.http.post<any>(url, payload);
  }

  /**
   * RCA Response Assign user list.
   * @param params country id to fetch the user list.
   * @returns An Observable of the rca response assign user.
   */
  getResponseAssignUserList(params: any): Observable<any> {
    const url = `${getApiUrl("RCA_RESPONSE_ASSIGN_USER")}`;
    return this.http.get<any>(url, { params });
  }

  /**
   * RCA response submit to assigned user.
   * @param payload user id.
   * @returns An Observable of the rca response.
   */
  submitRcaResponseToAssignee(
    payload: any,
    mappingId: number
  ): Observable<any> {
    const url = `${getApiUrl(
      "RCA_RESPONSE_ASSIGN_SUBMIT"
    )}/${mappingId}/assign`;
    return this.http.post<any>(url, payload);
  }

  /**
   * Upload RCA submit.
   * @param payload file data.
   * @returns An Observable of the rca response.
   */
  uploadRcaSubmit(payload: FormData): Observable<any> {
    const url = `${getApiUrl("UPLOAD_RCA_SUBMIT")}`;
    return this.http.post(url, payload);
  }

  /**
   * Upload RCA as draft.
   * @param payload file data.
   * @returns An Observable of the rca response.
   */
  uploadRcaDraft(payload: FormData): Observable<any> {
    const url = `${getApiUrl("UPLOAD_RCA_DRAFT")}`;
    return this.http.post(url, payload);
  }

  /**
   * Delete RCA.
   * @param rcaId.
   * @returns An Observable of the rca response.
   */
  deleteRcaAssessment(rcaId: number): Observable<any> {
    const url = `${getApiUrl("DELETE_RCA_ASSESSMENT")}/${rcaId}`;
    return this.http.delete(url);
  }

  /**
   * Share Rca.
   * @param payload user data.
   * @returns An Observable of the rca response.
   */
  shareRca(payload: any, rcaId: number): Observable<any> {
    const url = `${getApiUrl("SHARE_RCA_ASSESSMENT")}/${rcaId}`;
    return this.http.post(url, payload);
  }

  /**
   * Duplicate Rca.
   * @param rcaId.
   * @returns An Observable of the rca response.
   */
  duplicateRca(rcaId: number): Observable<any> {
    const url = `${getApiUrl("DUPLICATE_RCA_ASSESSMENT")}/${rcaId}`;
    return this.http.post(url, {});
  }

  /**
   * Downloads the RCA template file from the API.
   * @returns An Observable of the Blob containing the file data.
   */
  downloadRcaTemplate(): Observable<HttpResponse<Blob>> {
    const url = getApiUrl("DOWNLOAD_RCA_TEMPLATE");
    return this.http.get(url, { observe: 'response', responseType: 'blob' });
  }

  /**
  * Exports RCA response data from the backend based on filters.
  * @param rcaId The ID of the RCA assessment.
  * @param filters Optional filters to apply to the export.
  * @param zipped Whether to return the export as a ZIP archive (default: false).
  * @param historyId Optional history id to export a specific RCA version instead of the current version.
  * @returns An Observable of the Blob containing the file data.
  */
  exportRcaResponses(
    rcaId: number,
    filters?: any,
    zipped: boolean = false,
    historyId?: number | null
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (filters) {
      if (filters.country_filter && filters.country_filter.length > 0) {
        params = params.set('country_filter', filters.country_filter.join(','));
      }
      if (filters.ra_colleague_filter && filters.ra_colleague_filter.length > 0) {
        params = params.set('ra_colleague_filter', filters.ra_colleague_filter.join(','));
      }
      if (filters.impact_status_filter && filters.impact_status_filter.length > 0) {
        params = params.set('impact_status_filter', filters.impact_status_filter.join(','));
      }
      if (filters.response_status_filter && filters.response_status_filter.length > 0) {
        params = params.set('response_status_filter', filters.response_status_filter.join(','));
      }
      if (filters.date_from) {
        params = params.set('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params = params.set('date_to', filters.date_to);
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
    }
    params = params.set('zipped', zipped.toString());
    if (historyId) {
      params = params.set('history_id', historyId.toString());
    }
    const url = `${getApiUrl("API_RCA")}/${rcaId}/export-with-responses`;
    return this.http.get(url, { params, observe: 'response', responseType: 'blob' });
  }


  postReminderEmail(id: number, payload: any): Observable<any> {
    const url = `${getApiUrl(
      "RESPONSE_RCA_MAPPING_BY_ID"
    )}/${id}/send-reminder-mail`;
    return this.http.post<any>(url, payload);
  }

  changeDueDate(id: number, payload: any): Observable<any> {
    const url = `${getApiUrl(
      "EXTEND_RCA_DUE_DATE"
    )}/${id}`;
    return this.http.put<any>(url, payload);
  }

  exportRcaAssessment(payload: any): Observable<any> {
    const url = `${getApiUrl("EXPORT_RCA_ASSESSMENT")}`;
    return this.http.post(url, payload, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }
}
